import * as React from 'react';
import { useSyncExternalStore } from 'react';
import { getPendingCount, subscribePending } from '@/lib/request-progress';

/**
 * Barra ambiente de 3px no topo (Playbook § Feedback e progresso).
 *
 * Aparece sempre que existe operação assíncrona em andamento, sem que nenhum
 * componente precise saber disso: lê o contador compartilhado via
 * `useSyncExternalStore`. Zero prop-drilling de "loading".
 *
 * A curva é assintótica de propósito — sobe rápido até ~92% e só completa os
 * 100% quando a operação de fato termina. Uma barra que promete o fim antes da
 * hora ensina o usuário a não confiar nela.
 */
export function GlobalRequestProgress() {
  const pendentes = useSyncExternalStore(subscribePending, getPendingCount, () => 0);
  const ativo = pendentes > 0;

  const [progresso, setProgresso] = React.useState(0);
  const [visivel, setVisivel] = React.useState(false);

  React.useEffect(() => {
    if (ativo) {
      setVisivel(true);
      setProgresso((atual) => (atual === 0 ? 8 : atual));
      const intervalo = window.setInterval(() => {
        setProgresso((atual) => (atual >= 92 ? atual : atual + (92 - atual) * 0.18));
      }, 160);
      return () => window.clearInterval(intervalo);
    }

    if (!visivel) return;
    setProgresso(100);
    const saida = window.setTimeout(() => {
      setVisivel(false);
      setProgresso(0);
    }, 260);
    return () => window.clearTimeout(saida);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativo]);

  return (
    // Trilho em wash do primário, barra na cor de foco — a combinação do
    // sustenta-ai-app. O trilho só existe enquanto há operação em curso; uma
    // linha permanente no topo vira moldura e o olho para de registrá-la.
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-[3px] overflow-hidden transition-colors ease-standard"
      role="progressbar"
      aria-label="Carregando dados"
      aria-hidden={!visivel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={visivel ? Math.round(progresso) : undefined}
      style={{
        background: visivel ? 'var(--sys-primary-wash)' : 'transparent',
        transitionDuration: 'var(--duration-base)',
      }}
    >
      <div
        className="h-full bg-focus transition-[width,opacity] ease-standard"
        style={{
          width: `${progresso}%`,
          opacity: visivel ? 1 : 0,
          transitionDuration: 'var(--duration-base)',
        }}
      />
    </div>
  );
}
